<template>
  <v-dialog
    :model-value="isOpen"
    max-width="768px"
    persistent
    @close="handleClose"
  >
    <v-card>
      <!-- Header -->
      <v-card-title>
        <span>Traffic Allocation Test Results</span>
        <v-spacer />
        <v-btn
          icon
          @click="handleClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="mt-6">
        Run the function responsible for randomly picking
        a strategy based on its allocation percentage.
      </v-card-subtitle>

      <!-- Content -->
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model.number="leadCount"
              type="number"
              min="1"
              :rules="leadCountRules"
              label="Lead Count"
              outlined
            />
          </v-col>
        </v-row>

        <!-- Results Table -->
        <v-table v-if="hasResults">
          <thead>
            <tr>
              <th class="text-left">
                Strategy Name
              </th>
              <th class="text-center">
                Count
              </th>
              <th class="text-center">
                Percentage
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="result in simulationResults"
              :key="result.strategyName"
            >
              <td>{{ result.strategyName }}</td>
              <td class="text-center">
                {{ result.count }}
              </td>
              <td class="text-center">
                {{ result.percentage }}%
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Empty State -->
        <v-alert
          v-if="!hasResults && !isLoading"
          type="info"
          outlined
          class="mt-4"
        >
          Enter a lead count and click "Run Simulation" to see results.
        </v-alert>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="isLoading"
          :disabled="!isValidInput"
          @click="runSimulation"
        >
          Run Simulation
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'AbTestStrategyRoutingTester',

  props: {
    abTestId: {
      type: [Number, String],
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close'],

  data() {
    return {
      isLoading: false,
      leadCount: 1000,
      simulationResults: [],
    };
  },

  computed: {
    isValidInput() {
      return this.leadCount > 0 && Number.isInteger(this.leadCount);
    },

    hasResults() {
      return this.simulationResults.length > 0;
    },

    leadCountRules() {
      return [
        (v) => !!v || 'Lead count is required',
        (v) => v > 0 || 'Lead count must be greater than 0',
        (v) => Number.isInteger(v) || 'Lead count must be a whole number',
      ];
    },
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    async runSimulation() {
      if (!this.isValidInput) return;

      try {
        this.isLoading = true;
        this.simulationResults = []; // Reset results before running simulation

        const { data } = await this.$axios.post(
          `api/v1/simulate-ab-test/${this.abTestId}`,
          { lead_count: this.leadCount },
        );

        this.simulationResults = data;
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Failed to run simulation. Please try again.',
        });
      } finally {
        this.isLoading = false;
      }
    },

    handleClose() {
      this.$emit('close');
    },
  },
};
</script>
